import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const InstallContext = createContext(null);

// Cooldown: 14 days before re-showing after dismiss
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const DISMISS_KEY = 'bm_install_dismissed_at';
const FIRST_DASHBOARD_KEY = 'bm_first_dashboard_at';
const USAGE_DELAY_MS = 5 * 60 * 1000; // 5 minutes

function isStandalone() {
  // WebToNative bridge — user is inside the Play Store app
  if (typeof window !== 'undefined' && window.WTN) return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

function isIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  return isIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
}

function isAndroidBrowser() {
  const ua = navigator.userAgent;
  if (!/Android/i.test(ua)) return false;
  // Exclude WebView (WebToNative / Play Store app) and TWA
  if (/; wv\)/.test(ua) || /WebView/.test(ua)) return false;
  if (document.referrer.startsWith('android-app://')) return false;
  return true;
}

function isDismissedRecently() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = parseInt(raw, 10);
  if (isNaN(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
}

export function InstallProvider({ children }) {
  const deferredPromptRef = useRef(null);
  const [canInstallNative, setCanInstallNative] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);

  // Capture beforeinstallprompt globally (fires once per page load)
  useEffect(() => {
    if (installed) return;

    // Check if prompt was captured before React mounted (in index.js)
    if (window.__bm_deferred_prompt && !deferredPromptRef.current) {
      deferredPromptRef.current = window.__bm_deferred_prompt;
      setCanInstallNative(true);
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      window.__bm_deferred_prompt = e;
      setCanInstallNative(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detect if app was installed after prompt
    const installedHandler = () => {
      setInstalled(true);
      setCanInstallNative(false);
      deferredPromptRef.current = null;
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [installed]);

  // Re-check standalone on visibility change (user may have just installed)
  useEffect(() => {
    const check = () => { if (isStandalone()) setInstalled(true); };
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);

  const triggerInstall = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;
    prompt.prompt();
    const result = await prompt.userChoice;
    deferredPromptRef.current = null;
    setCanInstallNative(false);
    if (result.outcome === 'accepted') {
      setInstalled(true);
      return true;
    }
    return false;
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  const markDashboardVisit = useCallback(() => {
    if (!localStorage.getItem(FIRST_DASHBOARD_KEY)) {
      localStorage.setItem(FIRST_DASHBOARD_KEY, String(Date.now()));
    }
  }, []);

  const shouldShowPrompt = useCallback((hasCompletedOnboarding, hasActivePlan) => {
    if (installed) return false;
    if (!hasCompletedOnboarding || !hasActivePlan) return false;
    if (isDismissedRecently()) return false;

    // Must have 5 min of dashboard usage
    const firstVisit = localStorage.getItem(FIRST_DASHBOARD_KEY);
    if (!firstVisit) return false;
    if (Date.now() - parseInt(firstVisit, 10) < USAGE_DELAY_MS) return false;

    // Must have a way to install (native prompt, iOS Safari, or Android → Play Store)
    return canInstallNative || isIOSSafari() || isAndroidBrowser();
  }, [installed, canInstallNative]);

  return (
    <InstallContext.Provider value={{
      canInstallNative,
      isIOSSafari: isIOSSafari(),
      installed,
      triggerInstall,
      dismiss,
      markDashboardVisit,
      shouldShowPrompt,
    }}>
      {children}
    </InstallContext.Provider>
  );
}

export function useInstall() {
  return useContext(InstallContext);
}
